<div class="sj-dialog">
	<div mat-dialog-title class="sj-title">
		<div class="sj-title-text">
			<mat-icon class="sj-title-icon">{{ bankOffsetMode ? 'account_balance' : 'post_add' }}</mat-icon>
			<span>{{ bankOffsetMode ? 'Bank GL journal' : 'Quick journal entry' }}</span>
		</div>
		<button mat-icon-button type="button" class="sj-close" (click)="close()" aria-label="Close">
			<mat-icon>close</mat-icon>
		</button>
	</div>

	<div mat-dialog-content class="sj-body" *ngIf="!loading; else sjLoading">

		<!-- Bank + offset (two-line balanced) -->
		<ng-container *ngIf="bankOffsetMode">
			<p class="sj-lead text-muted small mb-3">
				Quick <strong>normal</strong> two-line journal: <strong>bank account</strong> plus <strong>offset GL</strong>. <strong>Post on bank line as</strong> sets debit or credit on the bank (the offset is the opposite). Amount must be greater than zero.
			</p>
			<p *ngIf="!bankAccountsList.length" class="text-warning small mb-2">
				No bank accounts were returned from the server. Try reloading Bank Reconciliation, or check bank account setup.
			</p>

			<form [formGroup]="accountForm" class="sj-form sj-form--bank">
				<div class="sj-row sj-row--bank-meta">
					<mat-form-field appearance="outline" class="sj-field sj-field--date">
						<mat-label>Transaction date</mat-label>
						<input matInput [matDatepicker]="sjPickerB" formControlName="trandate" autocomplete="off" />
						<mat-datepicker-toggle matSuffix [for]="sjPickerB"></mat-datepicker-toggle>
						<mat-datepicker #sjPickerB panelClass="sj-slim-dialog-datepicker-panel"></mat-datepicker>
					</mat-form-field>
					<div
						class="sj-field sj-select-wrap"
						[class.sj-select-wrap--invalid]="accountForm.get('department')?.invalid && accountForm.get('department')?.touched"
					>
						<label class="sj-select-label" for="sj-bank-dept">Department<span *ngIf="isDepartmentRequired()"> *</span></label>
						<select id="sj-bank-dept" class="sj-native-select" formControlName="department">
							<option [ngValue]="null">— None —</option>
							<option *ngFor="let tag of tags" [ngValue]="tag">{{ tag.description }}</option>
						</select>
						<span class="sj-select-error" *ngIf="accountForm.get('department')?.hasError('required') && accountForm.get('department')?.touched">
							Required when an expense GL account is used
						</span>
					</div>
				</div>

				<div class="sj-row sj-row--bank-account">
					<div
						class="sj-field sj-select-wrap"
						[class.sj-select-wrap--invalid]="accountForm.get('bankAccountCode')?.invalid && accountForm.get('bankAccountCode')?.touched"
					>
						<label class="sj-select-label" for="sj-bank-account">Bank account</label>
						<select id="sj-bank-account" class="sj-native-select" formControlName="bankAccountCode" (change)="onBankAccountChange()">
							<option *ngFor="let acc of bankAccountsList" [value]="acc.accountcode">
								{{ acc.bankaccountname }} ({{ acc.accountcode }})
							</option>
						</select>
						<span class="sj-select-hint" *ngIf="resolvedBankAccount?.accountcode">
							GL {{ resolvedBankAccount?.accountcode }} — {{ resolvedBankAccount?.accountname }}
						</span>
						<span class="sj-select-error" *ngIf="accountForm.get('bankAccountCode')?.hasError('required') && accountForm.get('bankAccountCode')?.touched">
							Select a bank account
						</span>
					</div>
					<div class="sj-field sj-select-wrap">
						<label class="sj-select-label" for="sj-bank-dir">Post on bank line as</label>
						<select id="sj-bank-dir" class="sj-native-select" formControlName="bankDirection">
							<option value="debit_bank">Debit bank, credit offset</option>
							<option value="credit_bank">Credit bank, debit offset</option>
						</select>
					</div>
				</div>

				<div class="sj-row sj-row--offset-amount">
					<div class="sj-offset-amount-lookup">
						<app-gl-account-lookup
							class="sj-lookup sj-lookup--inline"
							label="Offset GL account"
							[useInlineList]="true"
							(glselected)="addOffsetGl($event)"
							[reset]="resetOffsetLookup"
						></app-gl-account-lookup>
						<button *ngIf="accountForm.get('offsetAccount')?.value" mat-button type="button" class="sj-clear-offset" (click)="clearOffsetAccount()">Clear offset</button>
					</div>
					<mat-form-field appearance="outline" class="sj-field sj-field--amount-stacked">
						<mat-label>Amount</mat-label>
						<input matInput type="number" step="0.01" min="0.01" formControlName="amount" appSelectOnFocus />
					</mat-form-field>
				</div>

				<mat-form-field appearance="outline" class="sj-field sj-field--description full-width">
					<mat-label>Description</mat-label>
					<input matInput formControlName="description" placeholder="Narrative for both lines" appSelectOnFocus />
				</mat-form-field>

				<div class="sj-preview" *ngIf="bankOffsetPreview() as pv">
					<div class="sj-preview-title">Preview</div>
					<div class="sj-preview-line">
						<span class="sj-preview-acct">{{ resolvedBankAccount?.accountcode }}</span>
						<span class="sj-preview-side" [class.dr]="pv.bankAmt >= 0" [class.cr]="pv.bankAmt < 0">{{ pv.bankAmt >= 0 ? 'Debit' : 'Credit' }}</span>
						<span class="sj-preview-amt">{{ (pv.bankAmt >= 0 ? pv.bankAmt : -pv.bankAmt) | currency }}</span>
					</div>
					<div class="sj-preview-line" *ngIf="getOffsetAccount()">
						<span class="sj-preview-acct">{{ getOffsetAccount()?.accountcode }}</span>
						<span class="sj-preview-side" [class.dr]="pv.offsetAmt >= 0" [class.cr]="pv.offsetAmt < 0">{{ pv.offsetAmt >= 0 ? 'Debit' : 'Credit' }}</span>
						<span class="sj-preview-amt">{{ (pv.offsetAmt >= 0 ? pv.offsetAmt : -pv.offsetAmt) | currency }}</span>
					</div>
				</div>
			</form>
		</ng-container>

		<!-- Free-form multi-line -->
		<ng-container *ngIf="!bankOffsetMode">
			<p class="sj-lead text-muted small mb-3">
				Add lines below; debits must equal credits. Same posting API as General Ledger → Journal entry.
			</p>

			<form [formGroup]="accountForm" class="sj-form" (ngSubmit)="addValue()">
				<div class="sj-row sj-row--3">
					<mat-form-field appearance="outline" class="sj-field">
						<mat-label>Transaction date</mat-label>
						<input matInput [matDatepicker]="sjPicker" formControlName="trandate" />
						<mat-datepicker-toggle matSuffix [for]="sjPicker"></mat-datepicker-toggle>
						<mat-datepicker #sjPicker></mat-datepicker>
					</mat-form-field>
					<div class="sj-field sj-select-wrap">
						<label class="sj-select-label" for="sj-ql-type">Journal type</label>
						<select id="sj-ql-type" class="sj-native-select" formControlName="type">
							<option *ngFor="let type of journal_types" [value]="type">{{ type | uppercase }}</option>
						</select>
					</div>
					<div
						class="sj-field sj-select-wrap"
						[class.sj-select-wrap--invalid]="accountForm.get('department')?.invalid && accountForm.get('department')?.touched"
					>
						<label class="sj-select-label" for="sj-ql-dept">Department<span *ngIf="isDepartmentRequired()"> *</span></label>
						<select id="sj-ql-dept" class="sj-native-select" formControlName="department">
							<option [ngValue]="null">— None —</option>
							<option *ngFor="let tag of tags" [ngValue]="tag">{{ tag.description }}</option>
						</select>
						<span class="sj-select-error" *ngIf="accountForm.get('department')?.hasError('required') && accountForm.get('department')?.touched">
							Required for expense GL accounts
						</span>
					</div>
				</div>

				<app-gl-account-lookup
					class="sj-lookup"
					[useInlineList]="true"
					(glselected)="addGl($event)"
					[reset]="resetjournal"
					[preselected]="preselectedAccount"
				></app-gl-account-lookup>

				<div class="sj-row sj-row--2">
					<mat-form-field appearance="outline" class="sj-field sj-field--grow">
						<mat-label>Description</mat-label>
						<input matInput formControlName="description" placeholder="Line narrative" appSelectOnFocus />
					</mat-form-field>
					<div class="sj-field sj-select-wrap">
						<label class="sj-select-label" for="sj-ql-dr-cr">Debit / credit</label>
						<select id="sj-ql-dr-cr" class="sj-native-select" formControlName="entryType">
							<option value="debit">Debit</option>
							<option value="credit">Credit</option>
						</select>
					</div>
					<mat-form-field appearance="outline" class="sj-field sj-field--amount">
						<mat-label>Amount</mat-label>
						<input matInput type="number" step="0.01" min="0" formControlName="debitcredit" />
					</mat-form-field>
				</div>

				<div class="sj-actions-row">
					<button mat-flat-button color="accent" type="submit">{{ editingIndex !== null ? 'Update line' : 'Add line' }}</button>
					<button *ngIf="editingIndex !== null" mat-stroked-button type="button" (click)="cancelEdit()">Cancel edit</button>
					<button mat-stroked-button type="button" class="sj-open-full ml-auto" (click)="openFullJournal()">
						<mat-icon class="sj-open-full-icon">open_in_new</mat-icon>
						Full journal entry
					</button>
				</div>
			</form>

			<div class="sj-lines" *ngIf="debits_credits.length">
				<div
					class="sj-balance"
					[ngClass]="{ 'sj-balance--ok': isBalanced(), 'sj-balance--bad': !isBalanced() }"
				>
					<span *ngIf="!isBalanced()">Out of balance by {{ requiredToBalance() | currency }}</span>
					<span *ngIf="isBalanced()">In balance · {{ getDebitTotal() | currency }} DR / {{ getCreditTotal() | currency }} CR</span>
				</div>

				<div class="sj-line-list">
					<div
						class="sj-line"
						*ngFor="let d of debits_credits; let i = index"
						[class.sj-line--edit]="editingIndex === i"
					>
						<div class="sj-line-main">
							<span class="sj-line-acct">{{ d.account?.accountcode }} — {{ d.account?.accountname }}</span>
							<span class="sj-line-desc">{{ d.description }}</span>
						</div>
						<div class="sj-line-meta">
							<span class="sj-chip" [class.sj-chip--dr]="d.debitcredit >= 0" [class.sj-chip--cr]="d.debitcredit < 0">
								{{ d.debitcredit >= 0 ? 'DR' : 'CR' }}
							</span>
							<span class="sj-line-amt">{{ (d.debitcredit >= 0 ? d.debitcredit : -d.debitcredit) | currency }}</span>
							<button mat-icon-button type="button" (click)="editLine(d, i)" [disabled]="editingIndex !== null && editingIndex !== i" matTooltip="Edit">
								<mat-icon>edit</mat-icon>
							</button>
							<button mat-icon-button type="button" color="warn" (click)="removeDC(d)" matTooltip="Remove">
								<mat-icon>delete_outline</mat-icon>
							</button>
						</div>
					</div>
				</div>
			</div>

			<div class="sj-empty" *ngIf="!debits_credits.length">
				<mat-icon>playlist_add</mat-icon>
				<p>No lines yet. Pick an account, amount, and <strong>Add line</strong>.</p>
			</div>
		</ng-container>
	</div>

	<ng-template #sjLoading>
		<div mat-dialog-content class="sj-loading">
			<mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner>
			<span>Loading…</span>
		</div>
	</ng-template>

	<div mat-dialog-actions class="sj-footer" *ngIf="!loading">
		<button mat-button type="button" (click)="close()">Close</button>
		<button mat-stroked-button type="button" *ngIf="!bankOffsetMode" (click)="openFullJournal()">
			<mat-icon class="sj-open-full-icon">open_in_new</mat-icon>
			Full journal entry
		</button>
		<button
			mat-raised-button
			color="primary"
			type="button"
			[disabled]="
				posting ||
				(bankOffsetMode
					? accountForm.invalid || !bankOffsetPreview() || !accountForm.get('offsetAccount')?.value
					: !debits_credits.length || !isBalanced())
			"
			(click)="postJournal()"
		>
			<span *ngIf="posting" class="sj-posting"><mat-progress-spinner diameter="18" mode="indeterminate"></mat-progress-spinner> Posting…</span>
			<span *ngIf="!posting">Post journal</span>
		</button>
	</div>
</div>
